// Alistar Pulverize knockup VFX: lifts the target high, shows a ground shadow,
// then slams it down with an expanding impact ring and debris particles.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import { UnitVisual } from "../UnitVisual";

const RISE_MS   = 200;
const HANG_MS   = 120;
const FALL_MS   = 180;
const IMPACT_MS = 280;
const TOTAL = RISE_MS + HANG_MS + FALL_MS + IMPACT_MS;
const MAX_HEIGHT = 58;

// Fixed debris directions so we don't need RNG
const DEBRIS_ANGLES = [0, 40, 80, 130, 170, 210, 260, 310].map((d) => (d * Math.PI) / 180);

export class KnockupVFX implements Animation {
  private elapsed = 0;

  constructor(
    private visual: UnitVisual,
    private hexSize: number,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);

    const t = this.elapsed;
    let offsetY = 0;

    if (t < RISE_MS) {
      offsetY = -Easing.easeOutQuad(t / RISE_MS) * MAX_HEIGHT;
    } else if (t < RISE_MS + HANG_MS) {
      offsetY = -MAX_HEIGHT;
    } else if (t < RISE_MS + HANG_MS + FALL_MS) {
      const fallT = (t - RISE_MS - HANG_MS) / FALL_MS;
      offsetY = -MAX_HEIGHT * (1 - Easing.easeInCubic(fallT));
    }

    this.visual.bobOffset = offsetY;
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed;
    const cx = this.visual.x;
    const groundY = this.visual.y + this.hexSize * 0.35;

    // Ground shadow — shrinks as unit rises, grows back on fall
    const airborne = t < RISE_MS + HANG_MS + FALL_MS;
    if (airborne) {
      const heightFraction = Math.abs(this.visual.bobOffset) / MAX_HEIGHT;
      const shadowScale = 1 - heightFraction * 0.65;
      ctx.save();
      ctx.translate(cx, groundY);
      ctx.scale(1, 0.28);
      ctx.globalAlpha = 0.55 * heightFraction;
      ctx.fillStyle = "#000";
      ctx.beginPath();
      ctx.arc(0, 0, this.hexSize * 0.52 * shadowScale, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Impact ring + debris when landing
    const impactStart = RISE_MS + HANG_MS + FALL_MS;
    if (t >= impactStart) {
      const impT = (t - impactStart) / IMPACT_MS;
      const impAlpha = 1 - impT;
      const ringR = this.hexSize * 1.6 * Easing.easeOutQuad(impT);

      ctx.save();

      // Outer shockwave ring
      ctx.globalAlpha = impAlpha * 0.85;
      ctx.strokeStyle = "#c8a060";
      ctx.lineWidth = 4 * (1 - impT) + 1;
      ctx.shadowColor = "#c8a060";
      ctx.shadowBlur = 22;
      ctx.beginPath();
      ctx.arc(cx, groundY, ringR, 0, Math.PI * 2);
      ctx.stroke();

      // Inner ring
      ctx.globalAlpha = impAlpha * 0.5;
      ctx.lineWidth = 2;
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(cx, groundY, ringR * 0.5, 0, Math.PI * 2);
      ctx.stroke();

      // Debris chunks
      ctx.shadowBlur = 6;
      ctx.shadowColor = "#a07848";
      for (const angle of DEBRIS_ANGLES) {
        const dist = ringR * 0.75;
        const px = cx + Math.cos(angle) * dist;
        const py = groundY + Math.sin(angle) * dist * 0.35; // flatten to ground plane
        const chunkR = (2.8 * (1 - impT));
        if (chunkR <= 0) continue;
        ctx.globalAlpha = impAlpha * 0.8;
        ctx.fillStyle = "#a07848";
        ctx.beginPath();
        ctx.arc(px, py, chunkR, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }
  }

  isDone(): boolean {
    if (this.elapsed >= TOTAL) {
      this.visual.bobOffset = 0;
      return true;
    }
    return false;
  }
}
